/**
 * 古いブラウザに無い機能の補完(2026-09-16 改修依頼 F-2)。
 *
 * - すべて「その機能が無いときだけ」定義する(機能検出。OSやブラウザの種類・版では分岐しない)
 * - src/main.tsx の最初に読み込み、画面の処理より先に効かせる
 * - PDF表示(pdf.js)は別スレッド(Web Worker)でも動くため、ここの補完は届かない。
 *   pdf.js は補完入りの旧端末向けビルドを使っている(PdfView.tsx)
 */

type AnyRecord = Record<string, unknown>;

function define(target: object, name: string, value: unknown) {
  Object.defineProperty(target, name, { value, configurable: true, writable: true, enumerable: false });
}

// Promise.withResolvers(Safari 17.4 未満に無い)
if (typeof (Promise as unknown as AnyRecord).withResolvers !== 'function') {
  define(Promise, 'withResolvers', function withResolvers<T>(this: PromiseConstructor) {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new this<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  });
}

// Promise.try(Safari 18.2 未満に無い)
if (typeof (Promise as unknown as AnyRecord).try !== 'function') {
  define(Promise, 'try', function promiseTry<T>(this: PromiseConstructor, fn: (...args: unknown[]) => T, ...args: unknown[]) {
    return new this<T>((resolve) => resolve(fn(...args)));
  });
}

// URL.parse(Safari 18 未満に無い)。不正なURLは例外ではなく null を返す
if (typeof URL !== 'undefined' && typeof (URL as unknown as AnyRecord).parse !== 'function') {
  define(URL, 'parse', function parse(url: string | URL, base?: string | URL) {
    try {
      return base === undefined ? new URL(url) : new URL(url, base);
    } catch {
      return null;
    }
  });
}

// Array.prototype.at(Safari 15.4 未満に無い)
if (typeof (Array.prototype as unknown as AnyRecord).at !== 'function') {
  define(Array.prototype, 'at', function at(this: ArrayLike<unknown>, index: number) {
    const length = this.length >>> 0;
    let k = Math.trunc(Number(index)) || 0;
    if (k < 0) k += length;
    return k < 0 || k >= length ? undefined : this[k];
  });
}

// Object.groupBy(Safari 17.4 未満に無い)
if (typeof (Object as unknown as AnyRecord).groupBy !== 'function') {
  define(Object, 'groupBy', function groupBy<T>(items: Iterable<T>, callback: (item: T, index: number) => PropertyKey) {
    const groups = Object.create(null) as Record<PropertyKey, T[]>;
    let index = 0;
    for (const item of items) {
      const key = callback(item, index++);
      const k = typeof key === 'symbol' ? key : String(key);
      if (!groups[k]) groups[k] = [];
      groups[k].push(item);
    }
    return groups;
  });
}

// structuredClone(Safari 15.4 未満に無い)。データ(配列・オブジェクト・日付・Map・Set・バイナリ)の深いコピー
if (typeof (globalThis as unknown as AnyRecord).structuredClone !== 'function') {
  const cloneError = (what: string) => {
    const e = new Error(`${what} could not be cloned.`);
    e.name = 'DataCloneError';
    return e;
  };
  const clone = (value: unknown, seen: Map<unknown, unknown>): unknown => {
    if (value === null || typeof value !== 'object') {
      if (typeof value === 'function' || typeof value === 'symbol') throw cloneError(String(value));
      return value;
    }
    if (seen.has(value)) return seen.get(value);
    if (value instanceof Date) return new Date(value.getTime());
    if (value instanceof RegExp) return new RegExp(value.source, value.flags);
    if (value instanceof ArrayBuffer) {
      const copy = value.slice(0);
      seen.set(value, copy);
      return copy;
    }
    if (ArrayBuffer.isView(value)) {
      const view = value as unknown as { buffer: ArrayBuffer; byteOffset: number; length?: number; byteLength: number };
      const buffer = clone(view.buffer, seen) as ArrayBuffer;
      const Ctor = (value as unknown as { constructor: new (b: ArrayBuffer, o: number, l?: number) => unknown }).constructor;
      return value instanceof DataView
        ? new DataView(buffer, view.byteOffset, view.byteLength)
        : new Ctor(buffer, view.byteOffset, view.length);
    }
    if (value instanceof Map) {
      const copy = new Map();
      seen.set(value, copy);
      value.forEach((v, k) => copy.set(clone(k, seen), clone(v, seen)));
      return copy;
    }
    if (value instanceof Set) {
      const copy = new Set();
      seen.set(value, copy);
      value.forEach((v) => copy.add(clone(v, seen)));
      return copy;
    }
    if (Array.isArray(value)) {
      const copy: unknown[] = [];
      seen.set(value, copy);
      value.forEach((v, i) => {
        copy[i] = clone(v, seen);
      });
      return copy;
    }
    if (value instanceof Error) {
      const copy = new Error(value.message);
      copy.name = value.name;
      seen.set(value, copy);
      return copy;
    }
    const copy: AnyRecord = {};
    seen.set(value, copy);
    for (const key of Object.keys(value)) copy[key] = clone((value as AnyRecord)[key], seen);
    return copy;
  };
  define(globalThis, 'structuredClone', function structuredClone(value: unknown) {
    return clone(value, new Map());
  });
}

export {};
